import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiTrendingUp as TrendingUp, FiTrendingDown as TrendingDown } from 'react-icons/fi';

interface Props {
  value: number | string;
  label: string;
  trend?: string;
  trendValue?: string | number;
  format?: string;
  color?: string;
  displayValue?: string;
  link?: string;
}

const MetricWidget: React.FC<Props> = ({
  value,
  label,
  trend,
  trendValue,
  format = 'number',
  color = 'blue',
  displayValue,
  link
}) => {
  const navigate = useNavigate();

  const colorClasses: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    darkblue: 'text-blue-700 dark:text-blue-500',
    cyan: 'text-cyan-600 dark:text-cyan-400',
    darkcyan: 'text-cyan-700 dark:text-cyan-400',
    lightblue: 'text-blue-500 dark:text-blue-300',
    aqua: 'text-cyan-600 dark:text-cyan-400',
    purple: 'text-blue-600 dark:text-blue-400',
    teal: 'text-cyan-700 dark:text-cyan-400',
    green: 'text-green-600 dark:text-green-400',
    amber: 'text-amber-600 dark:text-amber-400',
    orange: 'text-orange-600 dark:text-orange-400',
    red: 'text-red-600 dark:text-red-400',
    pink: 'text-pink-600 dark:text-pink-400',
    gray: 'text-gray-600 dark:text-gray-400'
  };

  const formatValue = (val: number | string): string => {
    if (displayValue !== undefined) {
      return displayValue;
    }
    if (format === 'currency') {
      const numVal = Number(val);
      const fractionDigits = numVal < 10 ? 2 : 0;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      }).format(numVal);
    } else if (format === 'percentage') {
      return `${val}%`;
    } else if (format === 'number') {
      return new Intl.NumberFormat('en-US').format(Number(val));
    } else if (format === 'duration') {
      const duration = Number(val);
      return `${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(duration)}ms`;
    } else if (format === 'text') {
      return String(val);
    }
    return String(val);
  };

  const handleClick = (): void => {
    if (link) {
      navigate(link);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (link && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault();
      navigate(link);
    }
  };

  return (
    <div
      className={`flex flex-col h-full justify-center px-2 ${
        link ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors' : ''
      }`}
      onClick={link ? handleClick : undefined}
      onKeyDown={link ? handleKeyDown : undefined}
      role={link ? 'button' : undefined}
      tabIndex={link ? 0 : undefined}
    >
      <div className={`text-2xl font-bold ${colorClasses[color] || colorClasses.blue} transition-colors mb-1`}>
        {formatValue(value)}
      </div>
      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 transition-colors">
        {label}
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-2">
          {trend === 'up' ? (
            <TrendingUp className="w-3 h-3 text-cyan-500 dark:text-cyan-400" />
          ) : (
            <TrendingDown className="w-3 h-3 text-blue-400 dark:text-blue-300" />
          )}
          <span className={`text-xs font-medium ${trend === 'up' ? 'text-green-500 dark:text-green-400' : 'text-blue-500 dark:text-blue-400'}`}>
            {trendValue}
          </span>
        </div>
      )}
    </div>
  );
};

export default MetricWidget;
